import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Key, Trash, ShieldWarning, CircleNotch, X, LockKey, SignOut, ShieldCheck } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Account security card — sits inside SettingsPage.
 * Provides two destructive-ish actions guarded by password re-entry:
 *   1) Change password
 *   2) Delete account (nukes tenant + all its data, then logs out)
 */
export default function AccountSecuritySection() {
  const [pwdOpen, setPwdOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const refresh2fa = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setTwoFaEnabled(Boolean(data.enabled));
    } catch { /* noop */ }
  };
  useEffect(() => { refresh2fa(); }, []);

  const handleSignOut = async () => {
    try {
      await logout?.();
      toast.success("Odjavljeni ste.");
    } catch { /* noop */ }
    navigate("/login", { replace: true });
  };

  return (
    <section className="rounded-lg border border-border bg-card p-6" data-testid="account-security-section">
      <div className="flex items-center gap-2">
        <LockKey size={18} weight="duotone" className="text-primary" />
        <h3 className="text-base font-semibold text-foreground">Sigurnost računa</h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Upravljanje lozinkom, odjava iz sesije i brisanje računa. Brisanje trajno uklanja sve podatke — nema povrata.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/* Change password */}
        <button
          onClick={() => setPwdOpen(true)}
          className="flex items-center justify-between rounded-md border border-border bg-background p-4 text-left transition-colors hover:bg-muted"
          data-testid="btn-change-password"
        >
          <div>
            <p className="text-sm font-semibold text-foreground">Promijeni lozinku</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Zamijeni trenutnu novom.</p>
          </div>
          <Key size={20} weight="duotone" className="text-primary" />
        </button>

        {/* 2FA toggle */}
        <button
          onClick={() => setTwoFaOpen(true)}
          className={`flex items-center justify-between rounded-md border p-4 text-left transition-colors ${twoFaEnabled ? "border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10" : "border-border bg-background hover:bg-muted"}`}
          data-testid="btn-2fa"
        >
          <div>
            <p className={`text-sm font-semibold ${twoFaEnabled ? "text-emerald-700 dark:text-emerald-300" : "text-foreground"}`}>
              Dvofaktorska (2FA)
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {twoFaEnabled ? "✓ Aktivna — klikni za upravljanje" : "Preporučeno — dodatna razina sigurnosti"}
            </p>
          </div>
          <ShieldCheck size={20} weight={twoFaEnabled ? "fill" : "duotone"} className={twoFaEnabled ? "text-emerald-600" : "text-primary"} />
        </button>

        {/* Sign out */}
        <button
          onClick={handleSignOut}
          className="flex items-center justify-between rounded-md border border-border bg-background p-4 text-left transition-colors hover:bg-muted"
          data-testid="btn-sign-out"
        >
          <div>
            <p className="text-sm font-semibold text-foreground">Odjavi se</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Zatvori aktivnu sesiju.</p>
          </div>
          <SignOut size={20} weight="duotone" className="text-primary" />
        </button>

        {/* Delete account */}
        <button
          onClick={() => setDelOpen(true)}
          className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 p-4 text-left hover:border-destructive hover:bg-destructive/10"
          data-testid="btn-delete-account"
        >
          <div>
            <p className="text-sm font-semibold text-destructive">Obriši račun</p>
            <p className="mt-0.5 text-xs text-destructive/80">Trajno briše račun i podatke.</p>
          </div>
          <Trash size={20} weight="duotone" className="text-destructive" />
        </button>
      </div>

      {pwdOpen && <ChangePasswordDialog onClose={() => setPwdOpen(false)} />}
      {twoFaOpen && <TwoFactorDialog enabled={twoFaEnabled} onClose={() => { setTwoFaOpen(false); refresh2fa(); }} />}
      {delOpen && <DeleteAccountDialog onClose={() => setDelOpen(false)} />}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Change password dialog                                                     */
/* -------------------------------------------------------------------------- */
function ChangePasswordDialog({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) return setErr("Nova lozinka mora imati barem 8 znakova.");
    if (next !== confirm) return setErr("Nova lozinka i potvrda se ne podudaraju.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Lozinka je uspješno promijenjena.");
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Promjena lozinke" icon={<Key size={20} weight="duotone" className="text-primary" />} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Trenutna lozinka">
          <input type="password" required autoFocus value={current} onChange={(e) => setCurrent(e.target.value)} className="rw-input" data-testid="pwd-current" />
        </Field>
        <Field label="Nova lozinka" hint="Minimalno 8 znakova.">
          <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="rw-input" data-testid="pwd-new" />
        </Field>
        <Field label="Potvrda nove lozinke">
          <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rw-input" data-testid="pwd-confirm" />
        </Field>

        {err && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="pwd-error">{err}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted">Odustani</button>
          <button type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="pwd-submit">
            {saving && <CircleNotch size={14} className="animate-spin" />}
            Spremi lozinku
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Delete account dialog                                                       */
/* -------------------------------------------------------------------------- */
function DeleteAccountDialog({ onClose }) {
  const { tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canDelete = password.length > 0 && confirmation.trim().toUpperCase() === "OBRISI";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete("/auth/delete-account", { data: { password, confirmation } });
      toast.success("Račun je trajno obrisan.");
      // Auth cookies are cleared by the backend, but also flush AuthContext state.
      try { await logout?.(); } catch { /* noop */ }
      navigate("/login", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="Trajno brisanje računa"
      icon={<ShieldWarning size={20} weight="duotone" className="text-destructive" />}
      onClose={onClose}
      tone="destructive"
    >
      <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <p className="font-semibold">Ova radnja se ne može poništiti.</p>
        <ul className="mt-1.5 ml-4 list-disc space-y-0.5 text-[13px]">
          <li>Bit će obrisan tenant <b>{tenant?.brand_name || tenant?.name || "—"}</b>.</li>
          <li>Svi objekti, jedinice, rezervacije, gosti, računi i poruke bit će trajno izbrisani.</li>
          <li>Bit ćete odjavljeni i preusmjereni na login stranicu.</li>
        </ul>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <Field label="Vaša lozinka">
          <input type="password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="rw-input" data-testid="del-password" />
        </Field>
        <Field label='Napišite "OBRISI" za potvrdu' hint='Kopiranje je onemogućeno — upišite ručno velikim slovima.'>
          <input
            type="text"
            required
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            className="rw-input tracking-wider"
            placeholder="OBRISI"
            autoComplete="off"
            spellCheck={false}
            data-testid="del-confirmation"
          />
        </Field>

        {err && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="del-error">{err}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted">Odustani</button>
          <button
            type="submit"
            disabled={!canDelete || busy}
            className="flex items-center gap-1.5 rounded-md bg-destructive px-4 py-1.5 text-sm font-semibold text-white hover:bg-destructive/90 disabled:opacity-50"
            data-testid="del-submit"
          >
            {busy && <CircleNotch size={14} className="animate-spin" />}
            <Trash size={14} weight="bold" /> Trajno obriši račun
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared primitives                                                          */
/* -------------------------------------------------------------------------- */
function ModalShell({ title, icon, tone = "default", onClose, children }) {
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-2xl">
        <div className="mb-4 flex items-start gap-3">
          {icon}
          <div className="flex-1">
            <h4 className={`text-base font-semibold ${tone === "destructive" ? "text-destructive" : "text-foreground"}`}>{title}</h4>
          </div>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted" aria-label="Zatvori">
            <X size={16} weight="bold" />
          </button>
        </div>
        {children}
      </div>
    </>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* F7 — Two-factor authentication (TOTP) dialog                                */
/* -------------------------------------------------------------------------- */
function TwoFactorDialog({ enabled, onClose }) {
  // Three modes: idle → setup (showing QR) → verifying code
  const [mode, setMode] = useState(enabled ? "disable" : "idle");
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState("");

  const startSetup = async () => {
    setErr(""); setWorking(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSecret(data.secret);
      setUri(data.otpauth_uri);
      setMode("setup");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const verifyAndEnable = async (e) => {
    e.preventDefault();
    setErr(""); setWorking(true);
    try {
      await api.post("/auth/2fa/verify", { code: code.trim() });
      toast.success("2FA aktivirana — od sljedećeg logina trebat ćeš kod iz Authenticator-a.");
      onClose();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setWorking(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setErr(""); setWorking(true);
    try {
      await api.post("/auth/2fa/disable", { password, code: code.trim() || undefined });
      toast.success("2FA isključena.");
      onClose();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setWorking(false);
    }
  };

  const qrSrc = uri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(uri)}`
    : "";

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
            <ShieldCheck size={16} weight="duotone" className="text-primary" />
            Dvofaktorska autentikacija (2FA)
          </h3>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted"><X size={16} /></button>
        </div>

        {mode === "idle" && (
          <>
            <p className="text-sm text-muted-foreground">
              Dodatna razina sigurnosti — pri prijavi će tražiti 6-znamenkasti kod iz aplikacije Google Authenticator ili 1Password.
            </p>
            <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-foreground">
              <b>Trebat ćeš:</b>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                <li>Instalirati Google Authenticator (ili 1Password / Authy) na telefon</li>
                <li>Skenirati QR kod koji ti pokažemo</li>
                <li>Upisati prvi kod koji ti aplikacija generira</li>
              </ul>
            </div>
            {err && <p className="mt-3 text-xs text-destructive">{err}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="rw-btn-ghost">Odustani</button>
              <button onClick={startSetup} disabled={working} className="rw-btn-primary rw-btn-sm" data-testid="btn-2fa-start-setup">
                {working ? <CircleNotch size={14} className="animate-spin" /> : "Započni setup"}
              </button>
            </div>
          </>
        )}

        {mode === "setup" && (
          <form onSubmit={verifyAndEnable}>
            <p className="text-sm text-muted-foreground">
              Skeniraj QR kod s Authenticator aplikacijom:
            </p>
            <div className="mt-3 flex items-center justify-center rounded-md border border-border bg-white p-2">
              {qrSrc ? <img src={qrSrc} alt="2FA QR code" width={220} height={220} /> : <CircleNotch size={32} className="animate-spin text-muted-foreground" />}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Ne možeš skenirati? Upiši tajnu ručno u Authenticator:
            </p>
            <div className="mt-1 break-all rounded border border-border bg-muted/40 px-2 py-1 font-mono text-[11px] text-foreground">
              {secret}
            </div>
            <label className="mt-4 block text-xs font-medium text-foreground">
              Upiši trenutni 6-znamenkasti kod iz aplikacije:
            </label>
            <input
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="rw-input mt-1 w-full text-center font-mono text-lg tracking-widest"
              placeholder="123 456"
              inputMode="numeric"
              maxLength={6}
              data-testid="input-2fa-code"
            />
            {err && <p className="mt-2 text-xs text-destructive">{err}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rw-btn-ghost">Odustani</button>
              <button type="submit" disabled={working || code.length !== 6} className="rw-btn-primary rw-btn-sm" data-testid="btn-2fa-verify">
                {working ? <CircleNotch size={14} className="animate-spin" /> : "Aktiviraj 2FA"}
              </button>
            </div>
          </form>
        )}

        {mode === "disable" && (
          <form onSubmit={disable}>
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-xs text-emerald-700 dark:text-emerald-300">
              ✓ 2FA je trenutno <b>aktivna</b> na ovom računu.
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Da bi ju isključio, potvrdi lozinkom i važećim 2FA kodom.
            </p>
            <label className="mt-3 block text-xs font-medium text-foreground">Lozinka</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rw-input mt-1 w-full"
              required
              data-testid="input-2fa-disable-password"
            />
            <label className="mt-3 block text-xs font-medium text-foreground">2FA kod</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="rw-input mt-1 w-full text-center font-mono text-lg tracking-widest"
              placeholder="123 456"
              inputMode="numeric"
              maxLength={6}
              data-testid="input-2fa-disable-code"
            />
            {err && <p className="mt-2 text-xs text-destructive">{err}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rw-btn-ghost">Odustani</button>
              <button type="submit" disabled={working || !password || code.length !== 6}
                className="rw-btn-sm rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                data-testid="btn-2fa-disable-confirm">
                {working ? <CircleNotch size={14} className="animate-spin" /> : "Isključi 2FA"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

